import { useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Skeleton,
  Grid,
  Container,
  Autocomplete,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import DOMPurify from "dompurify";
import InfoIcon from "@mui/icons-material/Info";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomWindowsProductPictures from "./customWindowsProductPictures";
import EagleViewMapSection from "./eagleViewMapSection";
import PageTitle from "../../utils/pageTitle";
import defaultProductImg from "../../../img/default-image_420.png";
import ProductSpecificationFlat from "./productSpecificationFlat";
import SeoControl from "@/app/utils/seo/seoControl";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import {
  AddProductsResponse,
  CartItemRequest,
  GaEventDto,
  ProductBadgeResponse,
  ProductDetailsDocument,
  ProductDetailsSpecification,
  ProductDetailsUom,
  ProductDetailsVariant,
} from "../../models/product/types";
import dataService from "../../services/dataService";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { SidebarAddToList } from "../productList/sidebarAddToList";
import { RootState } from "../../store/store";
import { useDestinationWarehouseRedirect } from "@/app/hooks/useDestinationWarehouseRedirect";
import ProductListEnumType from "../../../enums/productListEnumType";
import { SidebarQuotedOrder } from "../productList/sidebarQuotedOrder";
import ProductBadge from "./badge";
import ProductSpecificationLines, {
  BoxSelectorData,
} from "./productSpacificationLines";
import PageLoading from "../../components/shared/pageLoading";
import { ColorMatchButton } from "./colorMatchButton";
import CheckNearByDialog from "./checkNearByDialog";
import BuilderIOSection from "../../builderio/components/builderIOSection";
import { BuilderIOSections, isEagleViewProduct } from "../../models/constants";
import { useGlobalNotification } from "../../components/header/GlobalNotificationContext";
import { loadFromStorage } from "@/app/utils/localStorageHelper/checkoutStorage";
import { STORAGE_KEYS } from "@/app/utils/localStorageHelper/checkoutKeys";
import ItemSourceEnum from "@/enums/itemSourceEnum";
import ProductRecommendations from "./productRecommendations";
import { sendGAEvent } from "@/app/utils/googleAnalytics/ga4";
import { isSAPSource } from "@/app/utils/erpHelper";

const registerUrl =
  process.env.REACT_APP_WEB_URL !== null
    ? process.env.REACT_APP_WEB_URL + "/register"
    : "";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export interface WarehouseProps {
  warehouseCode: string | null;
}

const Product: React.FC<WarehouseProps> = ({ warehouseCode }) => {
  const query = useQuery();
  const navigate = useNavigate();

  const isAuthenticated = useSelector(
    (state: RootState) => state.userState.isAuthenticated
  );
  const isShopProductsVisible = useSelector(
    (state: RootState) => state.userState.user.menu.shopProductsVisible
  );
  const hasEagleViewAccess = useSelector(
    (state: RootState) => state.userState.user.hasEagleViewAccess
  );
  const userId =
    useSelector((state: RootState) => state.userState.user.id)?.toString() ??
    "0";

  // Redirect to destination warehouse if specified in destinationWarehouseCode query parameter
  useDestinationWarehouseRedirect(isAuthenticated);

  // Page Logic
  let id: string | null = query.get("id") ?? null;
  let associatedproduct: string | null = query.get("associatedproduct") ?? null;
  let uomParam: string | null = query.get("uom") ?? null;

  const getLoginUrl = (productId: string | null, variantId: number | null) => {
    if (productId === null)
      return `${process.env.REACT_APP_WEB_URL}/quicklogin`;

    let returnUrl: string = `/p/${productId}`;

    if (variantId !== null) returnUrl += `?associatedproduct=${variantId}`;

    let returnUrlEncoded: string = encodeURIComponent(returnUrl);

    let quickLoginUrl: string = `${process.env.REACT_APP_WEB_URL}/quicklogin?returnUrl=${returnUrlEncoded}`;
    return quickLoginUrl;
  };

  const handleOnLogin = (
    productId: string | null,
    variantId: number | null
  ) => {
    window.location.href = getLoginUrl(productId, variantId);
  };

  const handleOnRegisterNow = () => {
    window.location.href = registerUrl;
  };

  const [isApiLoading, setIsAPILoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [nearByDialogOpen, setnearByDialogOpen] = useState<boolean>(false);

  const [parentSku, setParentSku] = useState<string | null>(null);
  const [sku, setSku] = useState<string | null>(null);
  const [sapproduct, setSAPProduct] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [brand, setBrand] = useState<string | null>(null);
  const [topSellingVariant, setTopSellingVariant] =
    useState<ProductDetailsVariant | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [variantType, setVariantType] = useState<string | null>(null);
  const [variants, setVariants] = useState<ProductDetailsVariant[]>([]);
  const [aboutText, setAboutText] = useState<string | null>(null);
  const [embeddedVideo, setEmbeddedVideo] = useState<string | null>(null);
  const [specifications, setSpecifications] = useState<
    ProductDetailsSpecification[]
  >([]);
  const [documents, setDocuments] = useState<ProductDetailsDocument[]>([]);
  const [pictures, setPictures] = useState<string[]>([]);
  const [windowGalleryPictures, setWindowGalleryPictures] = useState<string[]>(
    []
  );
  const [mainPicture, setMainPicture] = useState<string>(defaultProductImg);
  const [mainPictureOverlay, setMainPictureOverlay] = useState<string | null>(
    null
  );
  const [availability, setAvailability] = useState<string | null>(null);

  const [availableQuantity, setAvailableQuantity] = useState<number>(0);
  const [availableInBranch, setAvailableInBranch] = useState<string | null>(
    null
  );

  const [customWindows, setCustomWindows] = useState<boolean>(false);
  const [customRenaissance, setCustomRenaissance] = useState<boolean>(false);
  const [customWindowPrice, setCustomWindowPrice] = useState<string | null>(
    null
  );
  const [variantId, setVariantId] = useState<number | null>(null);
  const [productionLine, setProductLine] = useState<boolean>(false);
  const [productionLineRiserPan, setProductionLineRiserPan] =
    useState<boolean>(false);
  const [showColorMatchTool, setShowColorMatchTool] = useState<boolean>(false);

  const [hiddenPrice, setHiddenPrice] = useState<boolean>(false);

  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);

  const [quantity, setQuantity] = useState<number | string>(1);
  const [selectedUom, setSelectedUom] = useState<ProductDetailsUom | undefined>(
    undefined
  );
  const [uoMs, setUoms] = useState<ProductDetailsUom[]>([]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarOpenQuotedOrder, setSidebarOpenQuotedOrder] = useState(false);

  const [badgeText, setBadgeText] = useState<string | null>(null);
  const [isInCart, setIsInCart] = useState<boolean>(false);
  const [isInProductList, setIsInProductList] = useState<boolean>(false);
  const [isInQuoteList, setIsInQuoteList] = useState<boolean>(false);

  const [showCheckNearBy, setShowCheckNearBy] = useState<boolean>(false);
  const [boxData, setBoxData] = useState<BoxSelectorData | null>(null);

  const [specialOrderDialogOpen, setSpecialOrderDialogOpen] = useState(false);

  const impersonatedCustomer = useSelector(
    (state: RootState) => state.userState.user.impersonatedCustomer
  );

  let warehouseSource = useSelector(
    (state: RootState) => state.warehouseState?.selectedWarehouse?.source
  );

  let isSapWarehouse = isSAPSource(warehouseSource ?? "");

  useEffect(() => {
    if (!isShopProductsVisible && isAuthenticated) {
      navigate("/");
    }
  }, [isShopProductsVisible, navigate, isAuthenticated]);

  const { setNotification } = useGlobalNotification();

  const [hasQuotedOrders, setHasQuotedOrders] = useState(false);

  useEffect(() => {
    if (id) {
      setLoading(true);
      const timer = setTimeout(async () => {
        try {
          setQuantity(1);

          const response = await dataService.getProductDetails(
            id,
            warehouseCode,
            true
          );

          if (!response.success || !response.data) {
            setLoading(false);
            return;
          }

          const data = response.data;
          console.log(
            "[product] data.sku:",
            JSON.stringify(data.sku),
            "| data.variantDetails?.sku:",
            JSON.stringify(data.variantDetails?.sku)
          );

          // Block access to EagleView products for users without the permission
          if (isEagleViewProduct(data.sku) && !hasEagleViewAccess) {
            setLoading(false);
            navigate("/");
            return;
          }

          PageTitle(data.name ?? "Product Details");

          setName(data.name);
          setBrand(data.brand);
          setVariantType(data.type);
          setVariants(data.variants);
          setUoms(data.uoMs);
          setCategory(data.category);
          setAboutText(DOMPurify.sanitize(data.fullDescription ?? ""));
          setEmbeddedVideo(data.embeddedVideo);
          setDocuments(data.attachments);
          setCustomWindows(data.customWindows);
          setCustomRenaissance(data.customRenaissance);
          setHasQuotedOrders(data.hasQuotedOrders);

          if (data.customRenaissance || data.customWindows)
            setCustomWindowPrice(data.customWindowsPrice);

          if (isAuthenticated && warehouseCode) {
            if (!data.customRenaissance && !data.customWindows) {
              setUnitPrice(data.price?.priceValue);
              setHiddenPrice(data.hidePrice);
              setTotalPrice(data.price?.priceValue);
              setProductLine(data.productionLine);
              setProductionLineRiserPan(data.productionLineRiserPan);
              setShowCheckNearBy(data.enableCheckNearbyStore);
            }
          }

          if (!data.customRenaissance && !data.customWindows) {
            setShowColorMatchTool(data.showColorMatchTool);
          }

          setParentSku(data.variantDetails === null ? null : data.sku);

          if (data.variantDetails != null) {
            const variantDetails = data.variantDetails;
            setTopSellingVariant(
              data.variants.find(
                (x: ProductDetailsVariant) => x.id === variantDetails.id
              ) ?? null
            );
            applySelectedVariantProps(
              variantDetails.id,
              variantDetails.pictures,
              variantDetails.sku,
              variantDetails.sapProduct,
              variantDetails.overlayRGB,
              variantDetails.inventoryStatus
            );
            setSpecifications(variantDetails.specifications);
            if (isAuthenticated) {
              if (warehouseCode) {
                setUnitPrice(variantDetails.price.priceValue);
                setTotalPrice(variantDetails.price.priceValue);
                setBadge(variantDetails.productBadge);
                setShowCheckNearBy(variantDetails.enableCheckNearbyStore);
              }
              setHiddenPrice(variantDetails.hidePrice);
              setUoms(variantDetails.uoMs);
              const defaultUom = uomParam
                ? variantDetails.uoMs?.find((u) => u.id === Number(uomParam))
                : variantDetails.price?.uomFullName
                ? variantDetails.uoMs?.find(
                    (u) => u.fullName === variantDetails.price.uomFullName
                  )
                : variantDetails.uoMs?.find((u) => u.isPreSelected);

              setSelectedUom(defaultUom);

              await reloadAvailableQuantityInfo(
                variantDetails.id,
                defaultUom?.id ?? 0
              );
            }
          } else {
            const pictureURLs =
              data.pictures.length > 0
                ? data.pictures.map(
                    (x) =>
                      `${process.env.REACT_APP_CDN_URL}/images/general/picture_${x}`
                  )
                : [defaultProductImg];

            setPictures(pictureURLs);
            setMainPicture(pictureURLs[0]);

            const windowGalleryURLs = data.windowGallery.map(
              (x) =>
                `${process.env.REACT_APP_CDN_URL}/images/general/picture_${x}`
            );
            setWindowGalleryPictures(windowGalleryURLs);

            setSku(data.sku);
            setSAPProduct(data.sapProduct ?? "");
            setAvailability(data.inventoryStatus);
            setSpecifications(data.specifications);

            const defaultUom = uomParam
              ? data.uoMs?.find((u) => u.id === Number(uomParam))
              : data.price?.uomFullName
              ? data.uoMs?.find((u) => u.fullName === data.price.uomFullName)
              : data.uoMs?.find((u) => u.isPreSelected);
            setSelectedUom(defaultUom);

            if (isAuthenticated)
              await reloadAvailableQuantityInfo(
                Number(id),
                defaultUom?.id ?? 0
              );

            setBadge(data.productBadge);

            if (data.overlayRGB) {
              setMainPictureOverlay(data.overlayRGB);
            }

            setVariantId(null);
          }

          if (associatedproduct) {
            var selectedVariant = data.variants.find(
              (v) => v.id === Number(associatedproduct)
            );
            if (selectedVariant) {
              await getVariantDetails(selectedVariant);
            }
          } else {
            if (isAuthenticated)
              await dataService.sendProductDetailsPageSSE(
                data.variantDetails != null
                  ? data.variantDetails.id
                  : Number(id)
              );
          }

          setLoading(false);
        } catch (err) {
          console.error("Failed to fetch product details:", err);
          setLoading(false);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [id, associatedproduct, uomParam, warehouseCode]);

  useEffect(() => {
    const handlePopState = (event: any) => {
      sessionStorage.setItem("returnFromProductDetails", "true");
      navigate(-1);
    };

    window.history.pushState({}, "");
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  const setBadge = (badgeResponse: ProductBadgeResponse | null) => {
    if (badgeResponse) {
      setBadgeText(badgeResponse.badgeText);
      setIsInCart(badgeResponse.isInCart);
      setIsInProductList(badgeResponse.isInProductList);
      setIsInQuoteList(badgeResponse.isInQuoteList);
    }
  };

  const applySelectedVariantProps = (
    variantId: number,
    pictures: string[],
    sku: string | null,
    sapProduct: string | null,
    overlayRGB: string | null,
    inventoryStatus: string | null
  ) => {
    let picturesResponse: string[] = pictures;
    let pictureURLs: string[] = [];
    if (picturesResponse.length > 0)
      pictureURLs = picturesResponse.map(
        (x) => `${process.env.REACT_APP_CDN_URL}/images/general/picture_${x}`
      );
    else pictureURLs = [defaultProductImg];

    setPictures(pictureURLs);
    setMainPicture(pictureURLs[0]);

    setSku(sku);
    setSAPProduct(sapProduct);
    setAvailability(inventoryStatus);

    setWindowGalleryPictures([]);

    setVariantId(variantId);

    if (overlayRGB != null) setMainPictureOverlay(overlayRGB);
  };

  const handleItemQuantityChange = (value: string) => {
    if (value === "") {
      setQuantity("");
      return;
    }

    const numValue = Number(value);
    if (numValue > 0 && Number.isInteger(numValue)) {
      setQuantity(numValue);
      if (!productionLine) {
        setTotalPrice(numValue * unitPrice);
      }
    }
  };

  const handleQuantityBlur = () => {
    if (quantity === "" || (typeof quantity === "number" && quantity <= 0)) {
      setQuantity(1);
      setTotalPrice(unitPrice);
    }
  };

  const addProductToList = async (
    productListId: string,
    type?: ProductListEnumType
  ) => {
    if (!type) {
      return;
    }

    if (!selectedUom) {
      alert("Please select a UOM.");
      return;
    }
    setLoading(true);
    const productId =
      variantId && variantId !== 0 ? variantId : parseInt(id ?? "0");

    const items: CartItemRequest[] = [
      {
        productId: productId,
        attributeValueId: selectedUom.id,
        quantity: quantity,
      },
    ];
    let response;

    try {
      if (type === ProductListEnumType.QuoteList) {
        response = await dataService.AddProductToEstimate({
          estimateId: productListId,
          source: ItemSourceEnum.Product,
          products: items,
        });
      } else {
        response = await dataService.AddProductToTemplates({
          templateId: productListId,
          source: ItemSourceEnum.Product,
          products: items,
        });
      }

      const { success, data } = response || {};
      const {
        warnings = [],
        id,
        productListName: name,
        newProductListId,
        newProductListName,
      } = data || {};

      if (success && warnings.length === 0) {
        const listTypeText =
          type === ProductListEnumType.QuoteList
            ? "Estimate List"
            : "Product Template";
        const url = `${process.env.REACT_APP_WEB_URL}/ProductListItem/List/${id}`;

        if (type !== ProductListEnumType.BranchList) {
          setNotification(
            <>
              Successfully added the product to the {listTypeText}{" "}
              <a href={url}>{name}</a>.
            </>,
            "success"
          );
        } else {
          const newUrl = `${process.env.REACT_APP_WEB_URL}/ProductListItem/List/${newProductListId}`;
          setNotification(
            <>
              Successfully added the product to the {listTypeText}{" "}
              <a href={newUrl}>{newProductListName}</a>.{" "}
              <a href={url}>{name}</a> has been archived
            </>,
            "success"
          );
        }
      } else {
        setNotification(warnings[0] ?? "Failed to add product.", "error");
      }
    } catch (error) {
      setNotification(
        "An unexpected error occurred while adding the product.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVariantChange = async (
    event: React.SyntheticEvent,
    value: ProductDetailsVariant
  ) => {
    await getVariantDetails(value);
  };

  const getVariantDetails = async (value: ProductDetailsVariant) => {
    setTopSellingVariant(value);

    const response = await dataService.getVariantDetails(
      value.id,
      warehouseCode
    );
    const variantDetails = response.data;

    applySelectedVariantProps(
      variantDetails.id,
      variantDetails.pictures,
      variantDetails.sku,
      variantDetails.sapProduct,
      variantDetails.overlayRGB,
      variantDetails.inventoryStatus
    );

    if (isAuthenticated) {
      setUoms(variantDetails.uoMs);
      var selectedUOM =
        variantDetails.uoMs.find((uom) => uom.isPreSelected) ??
        variantDetails.uoMs[0];
      setSelectedUom(selectedUOM);
      if (warehouseCode) {
        setQuantity(1);
        setUnitPrice(variantDetails.price.priceValue);
        setTotalPrice(variantDetails.price.priceValue);
        setBadge(variantDetails.productBadge);
        setShowCheckNearBy(variantDetails.enableCheckNearbyStore);
      }
      setHiddenPrice(variantDetails.hidePrice);
      updateQueryParam("associatedproduct", variantDetails.id.toString());
      updateQueryParam("uom", selectedUOM.id.toString());

      await reloadAvailableQuantityInfo(variantDetails.id, selectedUOM.id);
      await dataService.sendProductDetailsPageSSE(value.id);
    }
    setLoading(false);
  };

  const handleUomChange = async (
    event: React.SyntheticEvent,
    newValue: ProductDetailsUom | null
  ) => {
    if (newValue) {
      if (!warehouseCode) {
        setSelectedUom(newValue);
        return;
      }

      const productId =
        variantId && variantId !== 0 ? variantId : parseInt(id ?? "0");
      const response = await dataService.fetchPriceByUom(
        productId,
        newValue.id
      );

      const priceDetails = response.data;
      setSelectedUom(newValue);
      setUnitPrice(priceDetails.priceValue);
      setTotalPrice(priceDetails.priceValue * quantity);
      updateQueryParam("uom", newValue.id.toString());

      await reloadAvailableQuantityInfo(productId, newValue.id);
    }
  };

  const reloadAvailableQuantityInfo = async (
    productId: number,
    uomId: number
  ) => {
    if (impersonatedCustomer && uomId > 0) {
      const productAvailableQuantityDto =
        await dataService.getProductAvailabilityQuantity(productId, uomId);
      if (
        productAvailableQuantityDto !== null &&
        productAvailableQuantityDto.data !== null
      ) {
        setAvailableQuantity(Number(productAvailableQuantityDto.data.total));
        setAvailableInBranch(productAvailableQuantityDto.data.warehouse);
      }
    }
  };

  const updateQueryParam = (key: string, value: string) => {
    const params = new URLSearchParams(window.location.search);
    params.set(key, value);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, "", newUrl);
  };

  const handleBoxChange = (data: BoxSelectorData) => {
    setBoxData(data);

    if (!data.lines || data.lines.length === 0) {
      return;
    }
    let totalQty = 0;
    data.lines.forEach((line) => {
      const { qty, lengthFt, lengthIn } = line;

      const totalInches = Number(lengthFt) * 12 + Number(lengthIn);

      if (data.boxType === "BOX/SQ" || data.boxType === "BOX/TT") {
        const fitQty =
          (totalInches * Number(qty) + 0.178 * Number(qty) * 12) / 12;
        totalQty += Math.ceil(fitQty);
      } else {
        const fitQty = (totalInches * Number(qty)) / 12;
        totalQty += Math.ceil(fitQty);
      }
    });

    if (totalQty != 0) {
      totalQty = Math.ceil(totalQty);
      setQuantity(totalQty);
      setTotalPrice(totalQty * unitPrice);
    }
  };

  const onAddToCartSuccess = () => {
    window.dispatchEvent(new Event("refresh-cart"));
    setNotification(
      <>
        The product has been added to your <a href="/cart">Shopping Cart</a>.
      </>,
      "success"
    );
  };

  const handleAddToCart = async () => {
    if (!selectedUom) {
      console.warn("Missing variant or UOM selection");
      return;
    }

    // EagleView cart-conflict checks
    if (hasEagleViewAccess) {
      const isCurrentProductEagleView = isEagleViewProduct(sku);

      const cartResponse = await dataService.getMiniShoppingCart();
      const cartItems = cartResponse.success
        ? cartResponse.data?.items ?? []
        : [];

      if (cartItems.length > 0) {
        const cartHasEagleView = cartItems.some((item) =>
          isEagleViewProduct(item.sku)
        );

        if (cartHasEagleView) {
          setNotification(
            <>
              Please finish your current EagleView purchase before adding other
              products to your <a href="/cart">Shopping Cart</a>.
            </>,
            "warning"
          );
          return;
        }

        if (isCurrentProductEagleView) {
          setNotification(
            <>
              Please finish your current purchase before adding the EagleView
              product to your <a href="/cart">Shopping Cart</a>.
            </>,
            "warning"
          );
          return;
        }
      }

      if (isCurrentProductEagleView) {
        const eagleViewAddress = loadFromStorage(
          userId,
          STORAGE_KEYS.EAGLEVIEW_ADDRESS_PREFILL
        );
        if (!eagleViewAddress) {
          setNotification(
            "Please select a property address on the map before adding this EagleView product to your cart.",
            "warning"
          );
          return;
        }
      }
    }

    setIsAPILoading(true);

    const productId =
      variantId && variantId !== 0 ? variantId : parseInt(id ?? "0");

    const items: CartItemRequest[] = [
      {
        productId: productId,
        attributeValueId: selectedUom.id,
        quantity: quantity,
        rib: boxData?.ribType,
        box: boxData?.boxType,
        cartSpecificationLines: boxData?.lines,
      },
    ];

    try {
      const response = await dataService.addProductsToCart(items);
      if (response.success) {
        var settingsRes = await dataService.getGASettings();
        var gaSettings = settingsRes.data;
        var addedItems = response.data.gaEvent?.items;

        if (!gaSettings || !addedItems) {
          console.log("GA: missing settings or event; skipping");
          return;
        }

        sendGAEvent("add_to_cart", {
          currency: "USD",
          value: Number(totalPrice),
          items: (addedItems ?? []).map((item, index) => ({
            item_id: item.itemId,
            item_name: item.itemName,
            affiliation: gaSettings?.storeName ?? "LansingNOW",
            index: String(index),
            item_brand: (item.manufacturers ?? []).join(", "),
            item_category: item.categories?.[0] ?? "",
            item_category2: item.categories?.[1] ?? "",
            item_category3: item.categories?.[2] ?? "",
            item_category4: item.categories?.[3] ?? "",
            item_category5: item.categories?.[4] ?? "",
            item_list_id: "",
            item_list_name: "",
            item_variant: item.itemName,
            location_id: "",
            price: String(unitPrice),
            quantity: String(quantity),
            coupon: "",
            discount: "",
          })),
        });

        if (availability === "Special Order") setSpecialOrderDialogOpen(true);
        else onAddToCartSuccess();
      } else if (response.error || response.message) {
        setNotification(<>{response.message || response.error}</>, "error");
      } else {
        console.error("Failed to add to cart");
      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setIsAPILoading(false);
    }
  };

  const handleCloseNearByDialog = () => {
    setnearByDialogOpen(false);
  };

  const handleOpenNearByDialog = async () => {
    setnearByDialogOpen(true);
  };

  if (loading) {
    <PageLoading />;
  }

  let clearwinUrl = `/${process.env.REACT_APP_SUBDIRECTORY}/programs/clearwin`;

  let commonPDPBuilderData = {
    categoryName: category,
    productName: name,
    availability: availability,
    customWindows: customWindows,
    customRenaissance: customRenaissance,
    brand: brand,
    clearwinUrl: clearwinUrl,
    sku:
      isSapWarehouse && !customWindows && !customRenaissance ? sapproduct : sku,
    isClearWin: customWindows,
    productId: variantId && variantId !== 0 ? variantId : id,
  };

  return (
    <>
      {isApiLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1800,
            pointerEvents: "auto",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#1976d2" }} />
        </Box>
      )}

      <div className="page-margin">
        <SeoControl noindex={true} nofollow={true} />
        <BuilderIOSection
          section={BuilderIOSections.PDPHeader}
          data={commonPDPBuilderData}
        />
        {/* <div className='text-center'>
                    <div className='page-title'>
                        <h1>{category}</h1>
                    </div>
                </div> */}
        <Container maxWidth="xl">
          <Grid container spacing={2}>
            <Grid item md={6} className="fullsizeTablet">
              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                sx={{ textAlign: "center" }}
              >
                {loading ? (
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    width={500}
                    height={500}
                  />
                ) : (
                  <div className="image-container">
                    <img
                      style={{
                        maxHeight: "500px",
                        maxWidth: "90%",
                        backgroundColor: mainPictureOverlay ?? "",
                      }}
                      src={mainPicture}
                      alt="Product main"
                    />
                    <ProductBadge
                      badgeText={badgeText}
                      isInCart={isInCart}
                      isInProductList={isInProductList}
                      isInQuoteList={isInQuoteList}
                    />
                  </div>
                )}
              </Grid>
              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                sx={{ textAlign: "center", mt: 3 }}
              >
                {pictures.length <= 1 ? (
                  <></>
                ) : (
                  <div className="productDetailsSecondaryContainer">
                    {pictures.map((picture, index) => (
                      <div
                        key={picture}
                        className="productDetailsSecondaryImage handCursor"
                        onClick={() => setMainPicture(picture)}
                        style={{
                          backgroundColor:
                            index === 0 && mainPictureOverlay
                              ? mainPictureOverlay
                              : "",
                        }}
                      >
                        <img src={picture} alt="Product carousel" />
                      </div>
                    ))}
                  </div>
                )}
              </Grid>
              {isEagleViewProduct(sku) && hasEagleViewAccess && (
                <Grid container sx={{ mt: 3, px: 1 }}>
                  <Grid item xs={12}>
                    <EagleViewMapSection />
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Grid item md={6}>
              {loading ? (
                <Skeleton variant="rectangular" animation="wave" height={50} />
              ) : (
                <>
                  <BuilderIOSection
                    section={BuilderIOSections.PDPProductName}
                    data={commonPDPBuilderData}
                  />
                  {/* <Typography variant="h4" gutterBottom className='pdp_productName' sx={{ mb: 5 }}>
                                {loading ? <Skeleton /> : <>{name}</>}
                            </Typography> */}
                  {customWindows || customRenaissance ? (
                    <>
                      <Box sx={{ mb: 1 }}>
                        <BuilderIOSection
                          section={BuilderIOSections.PDPProductAvailability}
                          data={commonPDPBuilderData}
                        />
                      </Box>

                      <Box sx={{ mb: 1 }}>
                        <BuilderIOSection
                          section={BuilderIOSections.PDPProductMainDetails}
                          data={commonPDPBuilderData}
                        />
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box
                        display="flex"
                        justifyContent="flex-start"
                        flexDirection="column"
                      >
                        <Box
                          display="flex"
                          justifyContent="flex-start"
                          sx={{
                            mb: 1,
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: "baseline",
                            maxHeight: { xs: "auto", md: "39px" },
                          }}
                        >
                          <BuilderIOSection
                            section={BuilderIOSections.PDPProductAvailability}
                            data={commonPDPBuilderData}
                          />

                          <Box
                            display="flex"
                            sx={{ flexDirection: { xs: "column", md: "row" } }}
                          >
                            {isAuthenticated && warehouseCode && (
                              <>
                                {!loading &&
                                  availability !== null &&
                                  (availableQuantity != null ||
                                    availableInBranch) && (
                                    <Typography
                                      sx={{ marginTop: { xs: 0, md: 0 } }}
                                      className="pdp_availability"
                                      style={{
                                        color:
                                          availability === "Stocked"
                                            ? "green"
                                            : availability ===
                                              "Stocked in Network"
                                            ? "green"
                                            : availability ===
                                                "Special Order" ||
                                              customWindows ||
                                              customRenaissance
                                            ? "red"
                                            : "",
                                      }}
                                      variant="body2"
                                      color="text.secondary"
                                      gutterBottom
                                    >
                                      {impersonatedCustomer ? (
                                        <>
                                          {"["}
                                          {availableQuantity != null &&
                                            `${availableQuantity} Available`}
                                          {availableQuantity > 0 &&
                                            availableInBranch &&
                                            ` @ ${availableInBranch}`}
                                          {"]"}
                                        </>
                                      ) : (
                                        <></>
                                      )}
                                    </Typography>
                                  )}{" "}
                                <></>
                                {showCheckNearBy && (
                                  <Button
                                    sx={{ margin: { xs: 1, md: 0 } }}
                                    className="blueButtonOutline w200 me-1 checkNearby pdp_checkFix"
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleOpenNearByDialog}
                                  >
                                    <div className="pdp_checkNearbyStore">
                                      <span> Check Nearby Store</span>
                                    </div>
                                  </Button>
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                          <BuilderIOSection
                            section={BuilderIOSections.PDPProductMainDetails}
                            data={commonPDPBuilderData}
                          />
                        </Box>
                      </Box>
                    </>
                  )}
                </>
              )}

              {customWindows || customRenaissance ? (
                <Typography
                  variant="body1"
                  gutterBottom
                  style={{ fontSize: "24px" }}
                >
                  {loading ? (
                    <Skeleton />
                  ) : (
                    <span style={{ letterSpacing: "4px", fontWeight: "500" }}>
                      <span>{customWindowPrice}</span>
                      <span style={{ color: "#777777" }}>
                        {"$".repeat(5 - (customWindowPrice ?? "").length)}
                      </span>
                    </span>
                  )}
                </Typography>
              ) : (
                <></>
              )}
              <div className="pdp_flexEnd">
                <div className="color-container">
                  {loading ? (
                    <Skeleton
                      variant="rectangular"
                      animation="wave"
                      height={40}
                    />
                  ) : (
                    <>
                      {variantType === null ? (
                        <></>
                      ) : (
                        <>
                          <div className="pdp_form-field">
                            <label>
                              {variantType}:{" "}
                              <span className="pdp_required-asterisk">*</span>
                            </label>
                            <p className="pdp_validation-message">
                              Please select a {variantType.toLowerCase()}.
                            </p>
                          </div>
                          <Autocomplete
                            options={variants}
                            value={topSellingVariant ?? undefined}
                            getOptionLabel={(option) => option.name}
                            groupBy={(option) => option.inventoryStatus}
                            onChange={handleVariantChange}
                            disableClearable
                            renderOption={(props, option) => {
                              const { key } = props;
                              return (
                                <Box
                                  component="li"
                                  {...props}
                                  key={key}
                                  className="autocompleteColorSelector"
                                >
                                  {variantType === "Color" ? (
                                    <Typography
                                      variant="body1"
                                      style={{
                                        backgroundColor: option.colorSquaresRGB,
                                        width: "15px",
                                        height: "15px",
                                        marginRight: "10px",
                                        border: "1px solid #d3d3d3",
                                      }}
                                    ></Typography>
                                  ) : (
                                    <></>
                                  )}
                                  <Typography
                                    variant="body1"
                                    color="textSecondary"
                                  >
                                    {option.name}
                                  </Typography>
                                </Box>
                              );
                            }}
                            renderGroup={(params) => (
                              <li key={params.key}>
                                <Typography
                                  sx={{
                                    padding: "5px 10px",
                                    pointerEvents: "none",
                                  }}
                                >
                                  <strong>{params.group}</strong>
                                </Typography>
                                <ul style={{ padding: 0 }}>
                                  {params.children}
                                </ul>
                              </li>
                            )}
                            renderInput={(params) => {
                              var colorSquaresRGB = variants.find(
                                (x) => x.name === params.inputProps.value
                              )?.colorSquaresRGB;

                              return (
                                <TextField
                                  {...params}
                                  inputProps={{
                                    ...params.inputProps,
                                    readOnly: true,
                                  }}
                                  variant="standard"
                                  sx={{ maxWidth: "380px" }}
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment:
                                      params.inputProps.value &&
                                      variantType === "Color" ? (
                                        <Typography
                                          variant="body1"
                                          style={{
                                            backgroundColor: colorSquaresRGB,
                                            width: "15px",
                                            height: "15px",
                                            marginRight: "10px",
                                            border: "1px solid #d3d3d3",
                                          }}
                                        ></Typography>
                                      ) : null,
                                  }}
                                />
                              );
                            }}
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
                {showColorMatchTool && (
                  <>
                    <ColorMatchButton productId={Number(id)} />
                  </>
                )}
              </div>
              {isAuthenticated &&
                (customRenaissance || customWindows ? (
                  <BuilderIOSection
                    section={BuilderIOSections.PDPRequestQuote}
                    data={commonPDPBuilderData}
                  />
                ) : (
                  <>
                    <div className="mt-40px">
                      {loading ? (
                        <Skeleton
                          variant="rectangular"
                          animation="wave"
                          height={40}
                        />
                      ) : (
                        <>
                          {!Array.isArray(uoMs) || uoMs.length === 0 ? (
                            <></>
                          ) : (
                            <>
                              <div className="pdp_form-field">
                                <label>
                                  UOM:{" "}
                                  <span className="pdp_required-asterisk">
                                    *
                                  </span>
                                </label>
                                <p className="pdp_validation-message">
                                  Please select a unit of measure.
                                </p>
                              </div>
                              <Autocomplete
                                options={uoMs}
                                value={selectedUom ?? undefined}
                                onChange={handleUomChange}
                                getOptionLabel={(option) => option.fullName}
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                disableClearable
                                renderOption={(props, option) => (
                                  <Box
                                    component="li"
                                    {...props}
                                    key={option.id}
                                  >
                                    <Typography
                                      variant="body1"
                                      color="textSecondary"
                                    >
                                      {option.fullName}
                                    </Typography>
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    inputProps={{
                                      ...params.inputProps,
                                      readOnly: true,
                                    }}
                                    variant="standard"
                                    sx={{ maxWidth: "380px" }}
                                  />
                                )}
                              />
                            </>
                          )}
                        </>
                      )}
                    </div>
                    <div className="mt-40px">
                      {!loading &&
                        warehouseCode &&
                        selectedUom &&
                        unitPrice != 0 && (
                          <Box sx={{ mt: 5 }}>
                            <Box className="pdp_flexBase">
                              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                $
                                {unitPrice.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </Typography>
                              <Typography
                                variant="h6"
                                color="text.secondary"
                                sx={{ fontWeight: 400, ml: 1 }}
                              >
                                {selectedUom.fullName}
                              </Typography>
                            </Box>

                            <Box
                              className="product-price pdp_flexStart"
                              sx={{
                                flexDirection: { xs: "column", md: "row" },
                                alignItems: { xs: "left", md: "center" },
                                mt: 2,
                              }}
                            >
                              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                $
                                {totalPrice.toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{" "}
                                Total
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "coulmn",
                                  marginLeft: { xs: 0, md: "40px" },
                                  marginTop: { xs: 1, md: 0 },
                                }}
                              >
                                <Box className="product-qty">
                                  <Typography variant="body1" sx={{ mr: 1 }}>
                                    QTY:
                                  </Typography>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    {(() => {
                                      const isEagleView =
                                        isEagleViewProduct(sku);
                                      return (
                                        <>
                                          <Button
                                            variant="text"
                                            size="small"
                                            disabled={isEagleView}
                                            onClick={() => {
                                              setQuantity((q) => {
                                                const newQty = Math.max(
                                                  1,
                                                  q - 1
                                                );
                                                setTotalPrice(
                                                  newQty * unitPrice
                                                );
                                                return newQty;
                                              });
                                            }}
                                          >
                                            <RemoveIcon
                                              sx={{
                                                fontSize: 16,
                                                color: "#494440",
                                                p: 0,
                                              }}
                                            />
                                          </Button>
                                          <TextField
                                            className="color-gray"
                                            type="number"
                                            value={quantity}
                                            disabled={isEagleView}
                                            onKeyDown={(e) => {
                                              if (
                                                [
                                                  "e",
                                                  "E",
                                                  "+",
                                                  "-",
                                                  ".",
                                                  ",",
                                                ].includes(e.key)
                                              ) {
                                                e.preventDefault();
                                              }
                                            }}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              if (
                                                value === "" ||
                                                Number.isInteger(Number(value))
                                              ) {
                                                handleItemQuantityChange(value);
                                              }
                                            }}
                                            onBlur={handleQuantityBlur}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                              width: 30,
                                              textAlign: "center",
                                            }}
                                            inputProps={{
                                              step: 1,
                                              min: 0,
                                            }}
                                          />
                                          <Button
                                            variant="text"
                                            size="small"
                                            disabled={isEagleView}
                                            onClick={() => {
                                              setQuantity((q) => {
                                                const newQty = q + 1;
                                                setTotalPrice(
                                                  newQty * unitPrice
                                                );
                                                return newQty;
                                              });
                                            }}
                                          >
                                            <AddIcon
                                              sx={{
                                                fontSize: 16,
                                                color: "#494440",
                                                p: 0,
                                              }}
                                            />
                                          </Button>
                                        </>
                                      );
                                    })()}
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        )}
                    </div>
                    {loading ? (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        height={100}
                      />
                    ) : (
                      <>
                        {productionLine && (
                          <ProductSpecificationLines
                            onChange={handleBoxChange}
                            productionLineRiserPan={productionLineRiserPan}
                          />
                        )}
                        {hiddenPrice ? (
                          <>
                            <div className="pdp_availability-container">
                              <span
                                className="pdp_availability-error"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "#D50000",
                                  gap: "6px",
                                }}
                              >
                                <InfoIcon
                                  sx={{ color: "#D50000", fontSize: 18 }}
                                />
                                Contact your branch to order this product.
                              </span>
                            </div>
                          </>
                        ) : !warehouseCode ||
                          unitPrice == 0 ||
                          unitPrice === null ||
                          unitPrice === undefined ? (
                          <>
                            <div className="pdp_availability-container">
                              <span className="pdp_availability-title">
                                Contact Branch for Pricing
                              </span>
                              <span className="pdp_availability-error">
                                Unable to add this product to cart - please try
                                again later
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="mt-40px">
                              <div style={{ marginTop: "32px" }}>
                                <Grid
                                  container
                                  spacing={1}
                                  direction="column"
                                  sx={{
                                    alignItems: { xs: "center", md: "start" },
                                  }}
                                >
                                  <Button
                                    className="blueButtonOutline product-details-button w200 me-1"
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleAddToCart}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 16 16"
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                        }}
                                      >
                                        <path
                                          className="shopping-cart-icon"
                                          fill="#54afe2"
                                          d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .49.598l-1 5a.5.5 0 0 1-.465.401l-9.397.472L4.415 11H13a.5.5 0 0 1 0 1H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5M3.102 4l.84 4.479 9.144-.459L13.89 4zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4m7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4m-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2m7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2"
                                        />
                                      </svg>
                                      <span
                                        style={{ textAlign: "center", flex: 1 }}
                                      >
                                        ADD TO CART
                                      </span>
                                    </div>
                                  </Button>
                                  {!isEagleViewProduct(sku) && (
                                    <Button
                                      id="addToTemplateButton"
                                      className="blueButtonOutline product-details-button w200 me-1"
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => {
                                        setSidebarOpen(true);
                                        setSidebarOpenQuotedOrder(false);
                                      }}
                                      style={{ marginTop: "8px" }}
                                    >
                                      ADD TO TEMPLATE OR ESTIMATE
                                    </Button>
                                  )}
                                  {hasQuotedOrders &&
                                    !isSapWarehouse &&
                                    !isEagleViewProduct(sku) && (
                                      <Button
                                        className="blueButtonOutline product-details-button w200 me-1"
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => {
                                          setSidebarOpenQuotedOrder(true);
                                          setSidebarOpen(false);
                                        }}
                                        style={{ marginTop: "8px" }}
                                      >
                                        Add To Quoted Order
                                      </Button>
                                    )}
                                  <SidebarAddToList
                                    name={name}
                                    sku={sku}
                                    availability={availability}
                                    imgUrl={mainPicture}
                                    mainPictureOverlay={mainPictureOverlay}
                                    open={sidebarOpen}
                                    onSave={(
                                      createdId?: string,
                                      productListType?: ProductListEnumType
                                    ) => {
                                      if (createdId && productListType) {
                                        addProductToList(
                                          createdId,
                                          productListType
                                        );
                                        setSidebarOpen(false);
                                      }
                                    }}
                                    onClose={() => {
                                      setSidebarOpen(false);
                                    }}
                                  />
                                  <SidebarQuotedOrder
                                    productId={
                                      variantId && variantId !== 0
                                        ? variantId
                                        : parseInt(id ?? "0")
                                    }
                                    quantity={quantity}
                                    uomId={selectedUom?.id ?? 0}
                                    name={name}
                                    sku={sku}
                                    availability={availability}
                                    imgUrl={mainPicture}
                                    mainPictureOverlay={mainPictureOverlay}
                                    open={sidebarOpenQuotedOrder}
                                    onClose={() => {
                                      setSidebarOpenQuotedOrder(false);
                                    }}
                                  />
                                </Grid>
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </>
                ))}
              {!isAuthenticated && (
                <Grid
                  container
                  spacing={2}
                  direction="column"
                  sx={{ mt: 3, maxWidth: "450px" }}
                >
                  <Grid item xs sx={{ mb: 2 }}>
                    <span style={{ fontWeight: 600 }}>
                      <a href={getLoginUrl(id, variantId)}>Log in</a> or{" "}
                      <a href={registerUrl}>Register</a> for price{" "}
                      <InfoOutlinedIcon
                        style={{ position: "relative", top: "5px" }}
                        color="primary"
                        fontSize="small"
                      />
                    </span>
                  </Grid>
                  <Grid item xs>
                    {loading ? (
                      <Skeleton height={40} />
                    ) : (
                      <>
                        <Button
                          sx={{ mb: 1 }}
                          className="blueButtonOutline w200 me-1"
                          onClick={() => handleOnLogin(id, variantId)}
                        >
                          LOG IN &raquo;
                        </Button>
                      </>
                    )}
                    {loading ? (
                      <Skeleton height={40} />
                    ) : (
                      <>
                        <Button
                          sx={{ mb: 1 }}
                          className="blueButtonOutline w200"
                          onClick={handleOnRegisterNow}
                        >
                          REGISTER NOW &raquo;
                        </Button>
                      </>
                    )}
                  </Grid>
                </Grid>
              )}
              {loading ? (
                <Skeleton variant="rectangular" animation="wave" height={100} />
              ) : (
                <>
                  <ProductSpecificationFlat
                    productId={variantId != null ? variantId : Number(id)}
                    brand={brand}
                    aboutText={aboutText}
                    embeddedVideo={embeddedVideo}
                    specifications={specifications}
                    documents={documents}
                    loading={loading}
                    commonPDPBuilderData={commonPDPBuilderData}
                  ></ProductSpecificationFlat>
                </>
              )}
            </Grid>
          </Grid>

          <CustomWindowsProductPictures
            windowGalleryPictures={windowGalleryPictures}
          />
        </Container>
      </div>
      {!loading && (
        <ProductRecommendations
          sku={parentSku ?? sku}
          warehouseCode={warehouseCode}
          brand={brand}
          isAuthenticated={isAuthenticated}
          productType={
            specifications?.find((x) => x.name === "Type")?.value ?? null
          }
        ></ProductRecommendations>
      )}
      {isAuthenticated && warehouseCode && (
        <CheckNearByDialog
          open={nearByDialogOpen}
          onClose={handleCloseNearByDialog}
          productId={variantId?.toString() ?? id}
        />
      )}

      <Dialog
        open={specialOrderDialogOpen}
        onClose={() => setSpecialOrderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography sx={{ textAlign: "center" }}>
            This is a special order item.
          </Typography>
          <Typography sx={{ textAlign: "center" }}>
            After completing your order, an associate will contact you to verify
            pricing, lead time, and any possible minimum quantity requirements.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            variant="outlined"
            color="primary"
            className="blueButtonOutline"
            onClick={() => {
              setSpecialOrderDialogOpen(false);
              onAddToCartSuccess();
            }}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Product;
